import { useState, FormEvent } from 'react';
import { getWhatsAppUrl } from '../../../components/feature/WhatsAppButton';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: 'residential',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('projectType', formData.projectType);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d7b6gq68177dosp0it10', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', projectType: 'residential', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 lg:py-32 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Left - Contact Info */}
          <div className="space-y-8 md:space-y-12">
            <div className="space-y-4 md:space-y-6">
              <p className="text-xs font-semibold tracking-widest text-[#D4AF37] uppercase">
                Get In Touch
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2A2A2A] leading-tight">
                Ready to Transform<br />
                Your Space?
              </h2>
              <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                Book a complimentary site inspection and discover how we can bring your vision to life with expert
                construction and refined design.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6 md:space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-[#D4AF37]/10 rounded-full flex-shrink-0">
                  <i className="ri-map-pin-line text-lg md:text-xl text-[#D4AF37]"></i>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Address
                  </p>
                  <p className="text-sm md:text-base text-[#2A2A2A]">
                    1 Wakis Road, Strydom Park<br />
                    Block E 1, Randburg<br />
                    Johannesburg, South Africa
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-[#D4AF37]/10 rounded-full flex-shrink-0">
                  <i className="ri-phone-line text-lg md:text-xl text-[#D4AF37]"></i>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Phone
                  </p>
                  <p className="text-xs text-gray-500 mb-0.5">Landline</p>
                  <a
                    href="tel:0118752075"
                    className="text-sm md:text-base text-[#2A2A2A] hover:text-[#D4AF37] transition-colors"
                  >
                    011 875 2075
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-[#25D366]/10 rounded-full flex-shrink-0">
                  <i className="ri-whatsapp-line text-lg md:text-xl text-[#25D366]"></i>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    WhatsApp
                  </p>
                  <a
                    href={getWhatsAppUrl('Hi Tadashi Consulting, I\'d like to enquire about your renovation services.')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm md:text-base text-[#2A2A2A] hover:text-[#25D366] transition-colors"
                  >
                    +27 65 215 1468
                  </a>
                  <p className="text-xs text-gray-500 mt-0.5">Instant replies</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-[#D4AF37]/10 rounded-full flex-shrink-0">
                  <i className="ri-mail-line text-lg md:text-xl text-[#D4AF37]"></i>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Email
                  </p>
                  <a
                    href="mailto:info@tadashiconsulting.co.za"
                    className="text-sm md:text-base text-[#2A2A2A] hover:text-[#D4AF37] transition-colors break-all"
                  >
                    info@tadashiconsulting.co.za
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10">
            <form
              id="contact-form"
              onSubmit={handleSubmit}
              data-readdy-form=""
              className="space-y-5 md:space-y-6"
            >
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                  placeholder="+27 XX XXX XXXX"
                />
              </div>

              <div>
                <label htmlFor="projectType" className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Type
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="residential">Residential Renovation</option>
                  <option value="commercial">Commercial Construction</option>
                  <option value="structural">Structural Upgrade</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, message: e.target.value });
                    }
                  }}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about your renovation project..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-3.5 md:py-4 bg-[#D4AF37] text-white text-sm md:text-base font-semibold rounded-full hover:bg-[#B8941F] transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Book Site Inspection'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    Thank you! We&apos;ll contact you shortly to schedule your site inspection.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    Something went wrong. Please try again or contact us directly.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
